import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Displays the live total price (selected variant price x quantity) for a product.
 * Recalculates whenever the variant selection or quantity changes.
 *
 * @typedef {Object} TotalPriceRefs
 * @property {HTMLElement} totalValue
 *
 * @extends {Component<TotalPriceRefs>}
 */
class TotalPriceComponent extends Component {
  requiredRefs = ['totalValue'];
  #abortController = new AbortController();
  #unitPrice = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#unitPrice = parseInt(this.dataset.unitPrice || '0', 10) || 0;
    this.#abortController = new AbortController();

    const { signal } = this.#abortController;
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate, { signal });

    document.addEventListener('input', this.#onQuantityInput, { signal });
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Finds the product form this total price block is tracking.
   * @returns {Element | null}
   */
  #getForm() {
    return document.querySelector(
      `product-form-component[data-section-id="${this.dataset.sectionId}"][data-product-id="${this.dataset.productId}"]`
    );
  }

  /**
   * Reads the current quantity from the associated product form, if any.
   * @param {number} [fallback]
   * @returns {number}
   */
  #getQuantity(fallback = 1) {
    const input = this.#getForm()?.querySelector('input[name="quantity"]');
    return (input instanceof HTMLInputElement && parseInt(input.value, 10)) || fallback;
  }

  /**
   * Handles variant changes, updating the tracked unit price.
   * @param {Event} event
   */
  #onVariantUpdate = (event) => {
    const detail = /** @type {any} */ (event).detail;

    if (detail.data.newProduct) {
      this.dataset.productId = detail.data.newProduct.id;
    } else if (detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { price, compare_at_price: compareAtPrice } = detail.resource ?? {};
    if (typeof price === 'number') {
      this.#unitPrice = this.#applyDiscount(price, compareAtPrice);
      this.dataset.unitSaving = String(price - this.#unitPrice);
    }

    this.#render();
  };

  /**
   * Previews the automatic collection discount that the block already matched in
   * Liquid. The rate is read from the block's data attributes rather than hardcoded,
   * so blocks/total-price.liquid stays the single place the percentage is set and the
   * server-rendered total and this client update cannot drift apart. Shopify applies
   * the real discount at checkout.
   * @param {number} price
   * @param {number} [compareAtPrice]
   * @returns {number}
   */
  #applyDiscount(price, compareAtPrice) {
    if (this.dataset.discountMetafieldMatches !== 'true') return price;

    // Mirrors the "Skip products already on sale" setting: a variant carrying its own
    // compare-at price keeps that sale price rather than stacking this discount on top.
    if (
      this.dataset.discountSkipWhenOnSale === 'true' &&
      typeof compareAtPrice === 'number' &&
      compareAtPrice > price
    ) {
      return price;
    }

    const discountValue = parseFloat(this.dataset.discountValue || '0');
    if (!discountValue) return price;

    const discountAmount =
      this.dataset.discountType === 'percentage'
        ? Math.round(price * (discountValue / 100))
        : Math.round(discountValue * 100);

    return Math.max(price - discountAmount, 0);
  }

  /**
   * Handles quantity changes from this product's own quantity selector.
   * @param {Event} event
   */
  #onQuantityUpdate = (event) => {
    const form = this.#getForm();
    if (!form || !(event.target instanceof Node) || !form.contains(event.target)) return;

    const detail = /** @type {any} */ (event).detail;
    this.#render(detail.quantity);
  };

  // Native +/- updates are immediate; typing a valid quantity previews before blur too.
  #onQuantityInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.name !== 'quantity' || !input.validity.valid) return;
    if (!this.#getForm()?.contains(input)) return;
    this.#render(Number(input.value));
  };

  /**
   * Recomputes and displays the total price.
   * @param {number} [quantityOverride]
   */
  #render(quantityOverride) {
    const quantity = quantityOverride ?? this.#getQuantity();
    const total = this.#unitPrice * quantity;
    const format =
      this.dataset.showCurrencyCode === 'true' ? this.dataset.moneyWithCurrencyFormat : this.dataset.moneyFormat;

    const money = amount => formatMoney(amount, format || '${{amount}}', this.dataset.currency || 'USD');
    this.refs.totalValue.textContent = money(total);
    const savingBadge = this.querySelector('[data-total-saving]');
    if (savingBadge) savingBadge.textContent = 'SAVE ' + money((Number(this.dataset.unitSaving) || 0) * quantity);
  }
}

if (!customElements.get('total-price-component')) {
  customElements.define('total-price-component', TotalPriceComponent);
}
